import type { ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import type { FeatureKey } from '@/lib/api/types';
import type { Permission } from '@/lib/auth/permissions';
import { AppShell } from './layout/AppShell';
import { NotFoundPage, RouteErrorPage } from './RouteError';
import { RequireSession } from './RequireSession';

/** Route rules the shell checks before rendering a page. */
export interface RouteHandle {
  feature?: FeatureKey;
  perm?: Permission;
  /** Platform admin console (unscoped session). */
  platform?: boolean;
  /** Open to teachers too (every other school route is office-only). */
  teacher?: boolean;
}

/** Code-split page: each screen is its own chunk. */
const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({ Component: (await load()).default });

const school = (path: string, load: () => Promise<{ default: ComponentType }>, handle: RouteHandle = {}): RouteObject => ({
  path,
  lazy: page(load),
  handle,
});

export const routes: RouteObject[] = [
  { path: '/login', lazy: page(() => import('@/features/auth/pages/LoginPage')) },
  { path: '/set-password', lazy: page(() => import('@/features/auth/pages/SetPasswordPage')) },
  {
    path: '/choose',
    element: <RequireSession />,
    children: [{ index: true, lazy: page(() => import('@/features/auth/pages/ChooseSchoolPage')) }],
  },
  {
    path: '/',
    element: <RequireSession />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, lazy: page(() => import('@/features/dashboard/DashboardPage')) },

          school('students', () => import('@/features/students/pages/StudentsPage')),
          school('students/import', () => import('@/features/students/pages/StudentImportPage'), { perm: 'write' }),
          school('students/:id', () => import('@/features/students/pages/StudentDetailPage')),
          school('notes', () => import('@/features/notes/pages/NotesPage'), { feature: 'teacherNotes', perm: 'approve' }),

          school('receive', () => import('@/features/payments/pages/ReceivePaymentPage'), { feature: 'fees', perm: 'write' }),
          school('payments', () => import('@/features/payments/pages/PaymentsPage'), { feature: 'fees' }),
          school('payments/:id', () => import('@/features/payments/pages/PaymentDetailPage'), { feature: 'fees' }),
          school('cash', () => import('@/features/cash/pages/CashPage')),
          school('fees/:tab?', () => import('@/features/fees/pages/FeesPage'), { feature: 'fees' }),

          school('staff', () => import('@/features/staff/pages/StaffPage'), { feature: 'payroll' }),
          school('staff/import', () => import('@/features/staff/pages/StaffImportPage'), { feature: 'payroll', perm: 'write' }),
          school('staff/:id', () => import('@/features/staff/pages/StaffDetailPage'), { feature: 'payroll' }),
          school('advances', () => import('@/features/advances/pages/AdvancesPage'), { feature: 'advances' }),
          school('advances/:id', () => import('@/features/advances/pages/AdvanceDetailPage'), { feature: 'advances' }),
          school('payroll', () => import('@/features/payroll/pages/PayrollPage'), { feature: 'payroll' }),
          school('payroll/:id', () => import('@/features/payroll/pages/PayrollRunPage'), { feature: 'payroll' }),
          school('payroll/payslips/:id', () => import('@/features/payroll/pages/PayslipPage'), { feature: 'payroll' }),

          school('attendance', () => import('@/features/attendance/pages/AttendanceTodayPage'), { feature: 'studentAttendance', teacher: true }),
          school('attendance/lessons/:classId/:date/:period', () => import('@/features/attendance/pages/LessonPage'), { feature: 'studentAttendance', teacher: true }),
          school('attendance/classes/:classId', () => import('@/features/attendance/pages/RosterPage'), { feature: 'studentAttendance', teacher: true }),
          school('attendance/report', () => import('@/features/attendance/pages/MonthReportPage'), { feature: 'studentAttendance' }),

          school('announcements', () => import('@/features/announcements/pages/AnnouncementsPage'), { feature: 'announcements', teacher: true }),
          school('grades', () => import('@/features/grading/pages/GradesPage'), { feature: 'examsGrades', teacher: true }),
          school('timetable', () => import('@/features/academics/pages/TimetablePage'), { feature: 'timetable', teacher: true }),
          school('new-year', () => import('@/features/rollover/pages/NewYearPage'), { perm: 'approve' }),
          school('settings/:section?', () => import('@/features/settings/pages/SettingsPage'), { perm: 'approve' }),

          { path: 'platform', lazy: page(() => import('@/features/platform/pages/PlatformOverviewPage')), handle: { platform: true } },
          { path: 'platform/schools', lazy: page(() => import('@/features/platform/pages/PlatformSchoolsPage')), handle: { platform: true } },
          { path: 'platform/people', lazy: page(() => import('@/features/platform/pages/PlatformPeoplePage')), handle: { platform: true } },
          { path: 'platform/people/:id', lazy: page(() => import('@/features/platform/pages/PlatformPersonPage')), handle: { platform: true } },
          { path: 'platform/codes', lazy: page(() => import('@/features/platform/pages/PlatformCodesPage')), handle: { platform: true } },
          { path: 'platform/schools/:id', lazy: page(() => import('@/features/platform/pages/PlatformSchoolPage')), handle: { platform: true } },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
